import { DatePipe } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { GridDataResult, PageChangeEvent, PagerPosition, PagerType } from "@progress/kendo-angular-grid";
import * as Sentry from "@sentry/browser";
import {
    AuthService,
    CategoriesService,
    DashboardsService,
    FirmsService,
    HelpersService,
    MappingOfStatusesService,
    IFirmRolesService,
    UsersService,
    SettingsBreadcrumbService,
} from "src/app/shared";
import { titleFromCard } from "../../shared/card-pipe/card-title.pipe";
import { AuditTrailEvent } from "../audit-trail-service/audit-trail-event.interface";
import { AuditTrailService } from "../audit-trail-service/audit-trail.service";
import { AuditTrailEventViewModel } from "./audit-trail-event-viewmodel.interface";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";
import { ApplicationsService } from "src/app/shared/applications-service/applications.service";
import { CardDisplayService } from "../../shared/card-display/card-display.service";

@Component({
    templateUrl: "./audit-trail.component.html",
    styleUrl: "./audit-trail.component.scss",
    standalone: false,
    host: { class: "wk-sat" },
})
export class AuditTrailComponent implements OnInit {
    private readonly auditTrailService = inject(AuditTrailService);
    private readonly helpersService = inject(HelpersService);
    private readonly authService = inject(AuthService);
    private readonly categoriesService = inject(CategoriesService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly firmsService = inject(FirmsService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly usersService = inject(UsersService);
    private readonly datePipe = inject(DatePipe);
    private readonly translateService = inject(TranslateService);
    private readonly applicationsService = inject(ApplicationsService);
    private readonly breadcrumbService = inject(SettingsBreadcrumbService);
    private readonly cardDisplayService = inject(CardDisplayService);

    currentLanguage = "en-CA";
    dashboards: any;
    gridData: AuditTrailEventViewModel[];
    auditsData: any;
    isLoading = false;
    selectedDashboard: string;
    selectedTimeFrame: number;
    pageSize = 14;
    pagerButtonCount = 0;
    pagerInfo = false;
    skip = 0;
    gridView: GridDataResult;
    public type: PagerType = "numeric";
    public pageSizes = false;
    public previousNext = false;
    public position: PagerPosition = "bottom";
    currentDashboard: IDashboardModel;

    constructor() {}

    async ngOnInit() {
        this.breadcrumbService.setBreadcrumbs("audittrail");
        if (!this.helpersService.userDataMerged) {
            await this.helpersService.mergeUserData();
        }

        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        // Get all Dashboards for populating component dropdown
        this.dashboards = await this.applicationsService.getAccessibleDashboards();

        // Get current application language
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang;
            if (this.auditsData) {
                this.gridData = this.updateViewModel(this.auditsData);
                this.skip = 0;
                this.loadItems();
            }
        });
        this.helpersService.suppressSpinner();
    }

    onDashboardChange(dashboardId: string) {
        this.selectedDashboard = dashboardId;
    }

    onTimeFrameChange(timeframe: string) {
        this.selectedTimeFrame = Number(timeframe);
    }

    refreshData(): void {
        const toDate = this.helpersService.NowUTC();
        const fromDate = this.helpersService.NowUTC();
        fromDate.setDate(fromDate.getDate() - this.selectedTimeFrame);
        this.isLoading = true;

        Sentry.startSpanManual(
            {
                name: "WebApp_Performance-SettingsAuditTrail",
                op: "SettingsAuditTrail",
            },
            async (span) => {
                this.auditTrailService
                    .getAuditTrail(fromDate, toDate, this.selectedDashboard, "Action,UserId,OperationTime,RowId")
                    .subscribe(async (data) => {
                        this.isLoading = false;
                        this.auditsData = data ? data.Audits : [];
                        this.gridData = this.updateViewModel(this.auditsData);
                        this.skip = 0;
                        this.loadItems();
                    });
                span?.setAttribute("Firm name", (await this.firmsService.getFirmById(this.authService.currentUser.firm_id)).Name);
                span?.setAttribute("Tax year", this.currentDashboard?.TaxYear);
                span?.end();
            },
        );
    }

    retrieveEntityTypeFromAction(action: string): string {
        let entityType = "";

        const possibleActions = ["card", "category", "role", "step", "user-dashboard-setting", "user"];

        entityType = possibleActions.find((possibleAction) => {
            return action.startsWith(possibleAction);
        });

        return entityType;
    }

    retrievePrettyName(entityType: string, entityId: string): string {
        let prettyName = entityId;

        switch (entityType) {
            case "card": {
                const card = this.cardDisplayService.getCardById(entityId);
                if (card !== undefined) {
                    prettyName = titleFromCard(card);
                }
                break;
            }
            case "category": {
                const category = this.categoriesService.getCategory(entityId);
                if (category) {
                    prettyName = category.Name[this.currentLanguage];
                }
                break;
            }
            case "role": {
                const role = this.iFirmRolesService.getRoleById(entityId);
                if (role) {
                    prettyName = role.Description[this.currentLanguage];
                }
                break;
            }
            case "step": {
                const steps = this.mappingOfStatusesService.Steps;
                const step = steps.find((s) => {
                    return s.Id === entityId;
                });
                if (step) {
                    prettyName = step.Name[this.currentLanguage];
                }
                break;
            }
            case "user-dashboard-setting": {
                const settings = this.helpersService.userDashboardSettingsService.UserDashboardSettings;
                const setting = settings.find((uds) => {
                    return uds.Id === entityId;
                });

                let udsUser = null;
                let udsDashboard = null;
                if (setting) {
                    udsUser = this.usersService.getUserById(setting.UserId, true);

                    udsDashboard = this.dashboards.find((dashboard) => {
                        return dashboard.Id === setting.DashboardId;
                    });
                }

                if (udsUser && udsDashboard) {
                    prettyName = udsUser.FirstName + " " + udsUser.LastName + " / " + udsDashboard.Title;
                }
                break;
            }
            case "user": {
                const user = this.usersService.getUserById(entityId, true);
                if (user) {
                    prettyName = user.FirstName + " " + user.LastName;
                }
                break;
            }
        }

        return prettyName;
    }

    updateViewModel(events: AuditTrailEvent[]): AuditTrailEventViewModel[] {
        return events
            .map((event) => {
                if (event.Action.startsWith("card")) {
                    return {
                        user: this.retrievePrettyName("user", event.UserId),
                        action: this.translateService.instant("AuditTrail.Events." + event.Action),
                        timestamp: this.datePipe.transform(event.OperationTime, "short"),
                        entity: this.retrievePrettyName(this.retrieveEntityTypeFromAction(event.Action), event.RowId),
                        operationTime: event.OperationTime,
                    };
                } else {
                    return {
                        user: this.retrievePrettyName("user", event.UserId),
                        action: this.translateService.instant("AuditTrail.Events." + event.Action),
                        timestamp: this.datePipe.transform(event.OperationTime, "short"),
                        entity: event.RowId,
                        operationTime: event.OperationTime,
                    };
                }
            })
            .flat()
            .sort((a: any, b: any) => {
                return new Date(b.operationTime).getTime() - new Date(a.operationTime).getTime();
            });
    }

    public pageChange(event: PageChangeEvent): void {
        this.skip = event.skip;
        this.loadItems();
    }

    private loadItems(): void {
        this.gridView = {
            data: this.gridData.slice(this.skip, this.skip + this.pageSize),
            total: this.gridData.length,
        };
        this.pagerButtonCount = this.gridData.length > 9 ? 10 : 0;
        this.pagerInfo = this.gridData.length > 1;
        this.previousNext = this.gridData.length > 9;
    }
}
